/**
 * FP&A, Fase 1B (ADR 0032): calendário, cenários, premissas, acessos e o motor único de
 * planejamento (Budget, Forecast e Base de Referência). Validações das entradas e os cálculos do
 * planejamento (cópia, reajuste percentual, média), sempre em decimal exato.
 */
import { z } from 'zod';
import { fromScaled, toScaled } from './money.ts';

export const FPA_PERIOD_STATUSES = ['future', 'open', 'closing', 'closed'] as const;
export const FPA_VERSION_KINDS = ['budget', 'forecast', 'reference'] as const;
export type FpaVersionKind = (typeof FPA_VERSION_KINDS)[number];
/**
 * Versões em que se lança: Budget e Forecast em rascunho, na ordem da lista. A primeira é a que
 * Meu planejamento mostra ao abrir e a que o cartão de bloqueios da conferência conta (ADR 0097).
 */
export const fpaOpenVersions = <V extends { status: string; kind: string }>(
  versions: readonly V[],
) => versions.filter((v) => v.status === 'draft' && (v.kind === 'budget' || v.kind === 'forecast'));

export const FPA_VERSION_STATUSES = [
  'draft',
  'in_approval',
  'published',
  'superseded',
  'archived',
] as const;
export const FPA_CONVERSION_BASES = ['budget', 'average', 'closing'] as const;
export const FPA_MONTH_STATES = [
  'actual_closed',
  'actual_partial',
  'projected',
  'no_data',
] as const;
export type FpaMonthState = (typeof FPA_MONTH_STATES)[number];
export const FPA_ASSUMPTION_KINDS = ['value', 'percent', 'quantity', 'index'] as const;
export const FPA_LINE_METHODS = [
  'manual',
  'copy',
  'percent',
  'average',
  'assumption',
  'import',
  'actual',
] as const;
/** Como a versão nova nasce a partir da versão de origem. */
/** rolling: Realizado da Base de Referência até o corte e, depois, outra versão (Budget/Forecast). */
export const FPA_COPY_METHODS = ['copy', 'percent', 'average', 'rolling'] as const;
export type FpaCopyMethod = (typeof FPA_COPY_METHODS)[number];

const code = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9][A-Z0-9_-]{1,29}$/, 'Use de 2 a 30 letras, números, - ou _');
const name = z.string().trim().min(1).max(200);
const optionalText = z
  .string()
  .trim()
  .max(1000)
  .nullish()
  .transform((v) => v || null);
const uuid = z.uuid();
/** Mês no formato AAAA-MM; vira o primeiro dia do mês (AAAA-MM-01). */
export const fpaMonth = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])(-01)?$/, 'Use o formato AAAA-MM')
  .transform((v) => `${v.slice(0, 7)}-01`);
/** Valor em texto decimal, até 4 casas (nunca ponto flutuante). */
export const fpaDecimal = z
  .string()
  .trim()
  .regex(/^-?\d{1,15}(\.\d{1,4})?$/, 'Use um número com até 4 casas decimais');
const currency = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{3}$/);

export const fpaFiscalYearInput = z.object({
  year: z.number().int().min(2000).max(2100),
  notes: optionalText,
});

export const fpaFiscalYearStatusInput = z.object({
  status: z.enum(['planning', 'open', 'closed']),
});

export const fpaPeriodsInput = z.object({
  legalEntityIds: z.array(uuid).min(1).max(100),
});

export const fpaPeriodStatusInput = z
  .object({
    status: z.enum(FPA_PERIOD_STATUSES),
    reason: optionalText,
  })
  .refine((v) => !v.reason || v.reason.length >= 3, {
    path: ['reason'],
    message: 'Explique em pelo menos 3 letras',
  });

export const fpaScenarioInput = z.object({
  code,
  name,
  description: optionalText,
});
export type FpaScenarioInput = z.infer<typeof fpaScenarioInput>;

export const fpaAssumptionInput = z
  .object({
    code,
    name,
    description: optionalText,
    kind: z.enum(FPA_ASSUMPTION_KINDS),
    unit: z
      .string()
      .trim()
      .max(30)
      .nullish()
      .transform((v) => v || null),
    currency: currency.nullish().transform((v) => v ?? null),
    // Sensível de pessoal (ex.: salário médio): valores só com fpa.payroll.read (ADR 0049, R14).
    isPayroll: z.boolean().default(false),
  })
  .refine((v) => (v.kind === 'value') === !!v.currency, {
    path: ['currency'],
    message: 'Premissa de valor exige a moeda; as demais não têm moeda',
  });
export type FpaAssumptionInput = z.infer<typeof fpaAssumptionInput>;

export const fpaAssumptionValueInput = z
  .object({
    assumptionId: uuid,
    scenarioId: uuid.nullish().transform((v) => v ?? null),
    legalEntityId: uuid.nullish().transform((v) => v ?? null),
    businessUnitId: uuid.nullish().transform((v) => v ?? null),
    validFrom: fpaMonth,
    value: fpaDecimal,
    replacesValueId: uuid.nullish().transform((v) => v ?? null),
    correctionReason: optionalText,
  })
  .refine((v) => !!v.replacesValueId === !!v.correctionReason, {
    path: ['correctionReason'],
    message: 'A correção exige o motivo',
  });

const accessReason = z.string().trim().min(3, 'Informe o motivo do acesso').max(1000);

export const fpaAccessScopeInput = z.object({
  userId: uuid,
  legalEntityId: uuid,
  costCenterId: uuid.nullish().transform((v) => v ?? null),
  includeChildren: z.boolean().default(true),
  /** Motivo do acesso (obrigatório; auditoria de 27/10). */
  notes: accessReason,
});

export const fpaAccessScopeUpdateInput = z.object({
  includeChildren: z.boolean(),
  isActive: z.boolean(),
  notes: accessReason,
});

export const fpaVersionInput = z
  .object({
    code: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z0-9][A-Z0-9._-]{1,39}$/, 'Use de 2 a 40 letras, números, ., - ou _'),
    name,
    description: optionalText,
    kind: z.enum(FPA_VERSION_KINDS),
    fiscalYearId: uuid,
    scenarioId: uuid.nullish().transform((v) => v ?? null),
    cutoffMonth: fpaMonth.nullish().transform((v) => v ?? null),
    presentationCurrency: currency,
    conversionBasis: z.enum(FPA_CONVERSION_BASES).default('budget'),
    mappingVersion: z
      .string()
      .trim()
      .max(100)
      .nullish()
      .transform((v) => v || null),
    /** Parte de outra versão (do mesmo exercício ou de outro, como a Base de Referência 2026). */
    base: z
      .object({
        versionId: uuid,
        method: z.enum(FPA_COPY_METHODS),
        /** Reajuste em %, para o método percentual (ex.: "5.5" = +5,5%). */
        percent: fpaDecimal.nullish().transform((v) => v ?? null),
        /** Meses da versão de origem que entram na média (AAAA-MM). */
        averageMonths: z.array(fpaMonth).max(24).default([]),
        /** Rolling: a versão que vale depois do corte da Base de Referência. */
        afterVersionId: uuid.nullish().transform((v) => v ?? null),
      })
      .refine((b) => b.method !== 'rolling' || b.afterVersionId !== null, {
        path: ['afterVersionId'],
        message: 'Escolha a versão que vale depois do corte',
      })
      .refine((b) => b.method !== 'percent' || b.percent !== null, {
        path: ['percent'],
        message: 'Informe o percentual de reajuste',
      })
      .refine((b) => b.method !== 'average' || b.averageMonths.length > 0, {
        path: ['averageMonths'],
        message: 'Escolha os meses da média',
      })
      .nullish()
      .transform((v) => v ?? null),
  })
  .refine((v) => v.kind !== 'reference' || v.cutoffMonth !== null, {
    path: ['cutoffMonth'],
    message: 'A Base de Referência exige o último mês com realizado',
  });
export type FpaVersionInput = z.infer<typeof fpaVersionInput>;

export const fpaVersionUpdateInput = z.object({
  name,
  description: optionalText,
  scenarioId: uuid.nullish().transform((v) => v ?? null),
  cutoffMonth: fpaMonth.nullish().transform((v) => v ?? null),
  presentationCurrency: currency,
  conversionBasis: z.enum(FPA_CONVERSION_BASES),
  mappingVersion: z
    .string()
    .trim()
    .max(100)
    .nullish()
    .transform((v) => v || null),
});

/** Dimensões de uma célula do planejamento (empresa, centro de custo, classificações). */
export const fpaLineKey = z.object({
  legalEntityId: uuid,
  costCenterId: uuid.nullish().transform((v) => v ?? null),
  businessUnitId: uuid.nullish().transform((v) => v ?? null),
  departmentId: uuid.nullish().transform((v) => v ?? null),
  managementAccountId: uuid.nullish().transform((v) => v ?? null),
  classificationId: uuid.nullish().transform((v) => v ?? null),
  expenseTypeId: uuid.nullish().transform((v) => v ?? null),
  classificationOriginal: z
    .string()
    .trim()
    .max(200)
    .nullish()
    .transform((v) => v || null),
  expenseTypeOriginal: z
    .string()
    .trim()
    .max(200)
    .nullish()
    .transform((v) => v || null),
  currency,
});
export type FpaLineKey = z.infer<typeof fpaLineKey>;

/**
 * Grava células do planejamento mês a mês. Valor vazio (null) retira a célula: sem dados não é
 * zero, e zero é um valor como outro qualquer.
 */
export const fpaLinesInput = z.object({
  lines: z
    .array(
      fpaLineKey.extend({
        month: fpaMonth,
        amount: fpaDecimal.nullable(),
        notes: optionalText,
      }),
    )
    .min(1)
    .max(2000),
});

export const fpaVersionMonthsInput = z.object({
  months: z
    .array(
      z.object({
        month: fpaMonth,
        state: z.enum(FPA_MONTH_STATES),
        note: optionalText,
      }),
    )
    .min(1)
    .max(24),
});

export const fpaVersionRateInput = z.object({
  month: fpaMonth.nullish().transform((v) => v ?? null),
  baseCurrency: currency,
  quoteCurrency: currency,
  rateType: z.enum(FPA_CONVERSION_BASES),
  rate: z
    .string()
    .trim()
    .regex(/^\d{1,10}(\.\d{1,10})?$/, 'Use uma taxa positiva com até 10 casas'),
  sourceRateId: uuid.nullish().transform((v) => v ?? null),
});

// ---------------------------------------------------------------------------
// Cálculos do planejamento (decimal exato, arredondamento comercial na 4ª casa)
// ---------------------------------------------------------------------------

/** Divide arredondando a metade para longe do zero. */
function divRound(n: bigint, d: bigint): bigint {
  const q = n / d;
  const r = n % d;
  if (r === 0n) return q;
  const up = (r < 0n ? -r : r) * 2n >= (d < 0n ? -d : d);
  if (!up) return q;
  return n < 0n !== d < 0n ? q - 1n : q + 1n;
}

/** Valor reajustado: valor × (1 + percentual / 100). "5.5" = +5,5%; "-10" = −10%. */
export function applyPercent(amount: string, percent: string): string {
  const hundred = 100n * 10n ** 4n; // 100% na escala de 4 casas
  return fromScaled(divRound(toScaled(amount) * (hundred + toScaled(percent)), hundred));
}

/** Média simples dos valores (meses sem dado não entram: ausência não é zero). */
export function averageAmount(amounts: readonly string[]): string | null {
  if (amounts.length === 0) return null;
  const sum = amounts.reduce((s, a) => s + toScaled(a), 0n);
  return fromScaled(divRound(sum, BigInt(amounts.length)));
}

/** Mês correspondente em outro exercício (2026-03-01 → 2027-03-01). */
export function shiftMonth(month: string, years: number): string {
  return `${Number(month.slice(0, 4)) + years}${month.slice(4)}`;
}

/** Os 12 meses de um exercício que começa em janeiro. */
export function monthsOfYear(year: number): string[] {
  return Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}-01`);
}

/**
 * Situação inicial dos meses da Base de Referência: realizado fechado até o mês anterior ao corte,
 * o mês de corte como realizado parcial e os seguintes projetados. Ex.: corte em setembro de 2026
 * = janeiro a agosto fechados, setembro parcial, outubro a dezembro projetados. O mês de corte
 * pode ser marcado como fechado depois, quando o fechamento for confirmado.
 */
export function referenceMonthStates(
  year: number,
  cutoffMonth: string,
): { month: string; state: FpaMonthState }[] {
  return monthsOfYear(year).map((month) => ({
    month,
    state:
      month < cutoffMonth
        ? 'actual_closed'
        : month === cutoffMonth
          ? 'actual_partial'
          : 'projected',
  }));
}

/**
 * Dimensões que identificam uma célula (sem o mês), com a origem: a linha avulsa e a linha gerada
 * por despesas na mesma combinação são células diferentes (ADR 0049, PR-3a).
 */
export function fpaCellKey(l: {
  legalEntityId: string;
  costCenterId: string | null;
  businessUnitId: string | null;
  departmentId: string | null;
  managementAccountId: string | null;
  classificationId: string | null;
  expenseTypeId: string | null;
  classificationOriginal: string | null;
  expenseTypeOriginal: string | null;
  currency: string;
  origin?: string | null;
}): string {
  return [
    l.legalEntityId,
    l.costCenterId,
    l.businessUnitId,
    l.departmentId,
    l.managementAccountId,
    l.classificationId,
    l.expenseTypeId,
    l.classificationOriginal,
    l.expenseTypeOriginal,
    l.currency,
    l.origin ?? 'manual',
  ]
    .map((v) => v ?? '')
    .join('|');
}

export interface FpaSourceLine {
  id: string;
  month: string;
  amount: string;
}

/**
 * Valores da versão nova a partir das linhas de uma célula da versão de origem:
 * - cópia: o mesmo mês do exercício de destino;
 * - percentual: o mesmo mês, reajustado;
 * - média: a média dos meses escolhidos, em todos os meses do exercício de destino.
 * Mês sem valor na origem continua sem valor (não vira zero).
 */
export function planFromBase(
  source: readonly FpaSourceLine[],
  opts: {
    method: FpaCopyMethod;
    targetYear: number;
    percent?: string | null;
    averageMonths?: readonly string[];
  },
): {
  month: string;
  amount: string;
  sourceLineId: string | null;
  memory: Record<string, unknown>;
}[] {
  if (opts.method === 'average') {
    const picked = source.filter((l) => opts.averageMonths?.includes(l.month));
    const avg = averageAmount(picked.map((l) => l.amount));
    if (avg === null) return [];
    return monthsOfYear(opts.targetYear).map((month) => ({
      month,
      amount: avg,
      sourceLineId: null,
      memory: { months: picked.map((l) => l.month).sort(), lines: picked.map((l) => l.id) },
    }));
  }
  return source.map((l) => {
    const month = shiftMonth(l.month, opts.targetYear - Number(l.month.slice(0, 4)));
    return opts.method === 'percent'
      ? {
          month,
          amount: applyPercent(l.amount, opts.percent ?? '0'),
          sourceLineId: l.id,
          memory: { base: l.amount, percent: opts.percent },
        }
      : { month, amount: l.amount, sourceLineId: l.id, memory: { base: l.amount } };
  });
}

// ---------------------------------------------------------------------------
// Câmbio da versão (ADR 0032, seção 5; ADR 0049, PR-3d)
// ---------------------------------------------------------------------------

export interface FpaVersionRateLike {
  month: string | null;
  baseCurrency: string;
  quoteCurrency: string;
  rateType: string;
  rate: string;
}

/**
 * Taxa da versão para levar `currency` à moeda de apresentação no mês, pelo critério da versão:
 * a do próprio mês tem preferência; sem ela, vale a do exercício inteiro (sem mês). Mesma moeda:
 * "1". Sem taxa: null (o valor fica de fora do total convertido e a conferência aponta).
 */
export function versionRateFor(
  rates: readonly FpaVersionRateLike[],
  basis: string,
  currency: string,
  presentation: string,
  month: string,
): string | null {
  if (currency === presentation) return '1';
  const ok = rates.filter(
    (r) => r.baseCurrency === currency && r.quoteCurrency === presentation && r.rateType === basis,
  );
  const m = month.slice(0, 7);
  return (
    ok.find((r) => r.month?.slice(0, 7) === m)?.rate ??
    ok.find((r) => r.month === null)?.rate ??
    null
  );
}

/** Valor × taxa (até 10 casas), com 4 casas e a metade arredondada para longe do zero. */
export function convertWithRate(amount: string, rate: string): string {
  const scale = 10n ** 10n;
  return fromScaled(divRound(toScaled(amount) * scaledRate(rate), scale));
}

function scaledRate(rate: string): bigint {
  const [int, frac = ''] = rate.trim().split('.');
  return BigInt(int!) * 10n ** 10n + BigInt((frac + '0000000000').slice(0, 10));
}

/** Valor ÷ taxa: a mesma taxa no sentido inverso (USD→BRL usada para BRL→USD), sem taxa nova. */
export function convertByInverseRate(amount: string, rate: string): string {
  const r = scaledRate(rate);
  if (r === 0n) throw new Error('taxa zero');
  return fromScaled(divRound(toScaled(amount) * 10n ** 10n, r));
}

/** Uma taxa encontrada e o sentido em que ela converte (direta = valor × taxa). */
export interface FpaPresentationRate {
  rate: string;
  inverse: boolean;
}

/**
 * Taxa da versão para apresentar em outra moeda: a direta (moeda → apresentação) ou, sem ela, a do
 * par inverso, que o cadastro guarda num sentido só (USD→BRL serve para BRL→USD). Nunca triangula
 * por uma terceira moeda nem inventa: sem as duas, devolve null (pendência de conversão).
 */
export function presentationRateFor(
  rates: readonly FpaVersionRateLike[],
  basis: string,
  currency: string,
  presentation: string,
  month: string,
): FpaPresentationRate | null {
  const direct = versionRateFor(rates, basis, currency, presentation, month);
  if (direct) return { rate: direct, inverse: false };
  const inverse = versionRateFor(rates, basis, presentation, currency, month);
  return inverse && scaledRate(inverse) !== 0n ? { rate: inverse, inverse: true } : null;
}

/** Converte pelo sentido da taxa encontrada. */
export function convertPresentation(amount: string, r: FpaPresentationRate): string {
  return r.inverse ? convertByInverseRate(amount, r.rate) : convertWithRate(amount, r.rate);
}
